import { useThemeColors } from '@/styles';
import { typography, spacing, radius } from '@/styles/design-tokens';
import { useOrgUsersQuery } from '@/hooks/queries';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { UserRole } from '@/types/enums/UserRole';

type ViewMode = 'user' | 'role';

interface LearningProgressAdminHeaderProps {
  selectedUserId: string | undefined;
  selectedRole: string | undefined;
  onUserSelect: (userId: string | undefined) => void;
  onRoleSelect: (role: string | undefined) => void;
}

export function LearningProgressAdminHeader({
  selectedUserId,
  selectedRole,
  onUserSelect,
  onRoleSelect,
}: LearningProgressAdminHeaderProps) {
  const colors = useThemeColors();
  const { activeOrganizationId } = useActiveOrganization();
  const { data: orgUsersData } = useOrgUsersQuery(activeOrganizationId);

  const viewMode: ViewMode = selectedRole ? 'role' : 'user';

  const users = orgUsersData?.users || [];
  const mentors = users.filter(u => u.role === UserRole.MENTOR);
  const students = users.filter(u => u.role === UserRole.STUDENT);

  const handleModeChange = (mode: ViewMode) => {
    if (mode === 'user') {
      onUserSelect(undefined);
    } else {
      onRoleSelect(UserRole.STUDENT);
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onUserSelect(value === '' ? undefined : value);
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onRoleSelect(value === '' ? undefined : value);
  };

  const selectStyle: React.CSSProperties = {
    backgroundColor: colors.offWhite,
    color: colors.text.primary,
    borderColor: colors.border.default,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: radius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.sizes.sm,
    outline: 'none',
    minWidth: 180,
  };

  const toggleStyle = (active: boolean): React.CSSProperties => ({
    padding: `6px ${spacing.sm}`,
    fontSize: typography.sizes.sm,
    fontWeight: 500,
    borderRadius: radius.sm,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: active ? colors.primary : 'transparent',
    color: active ? colors.text.inverse : colors.text.secondary,
    transition: 'all 0.15s ease',
  });

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span
        className="text-sm font-medium"
        style={{ color: colors.text.secondary }}
      >
        View stats for:
      </span>

      {/* Mode toggle — mentors & admins */}
      <div
        className="flex items-center"
        style={{
          backgroundColor: colors.background,
          border: `1px solid ${colors.border.default}`,
          borderRadius: radius.md,
        }}
      >
        <button
          type="button"
          style={toggleStyle(viewMode === 'user')}
          onClick={() => handleModeChange('user')}
        >
          By User
        </button>
        <button
          type="button"
          style={toggleStyle(viewMode === 'role')}
          onClick={() => handleModeChange('role')}
        >
          By Role
        </button>
      </div>

      {viewMode === 'user' ? (
        <select
          value={selectedUserId || ''}
          onChange={handleUserChange}
          style={selectStyle}
        >
          <option value="">My Stats</option>
          {mentors.length > 0 && (
            <optgroup label="Mentors">
              {mentors.map(u => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.username}
                </option>
              ))}
            </optgroup>
          )}
          {students.length > 0 && (
            <optgroup label="Students">
              {students.map(u => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.username}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      ) : (
        <select
          value={selectedRole || ''}
          onChange={handleRoleChange}
          style={selectStyle}
        >
          <option value="">Select role...</option>
          <option value={UserRole.STUDENT}>All Students</option>
          <option value={UserRole.MENTOR}>All Mentors</option>
        </select>
      )}
    </div>
  );
}
